import { FunctionComponent, useEffect, useState } from 'react';
import { ApplyScope } from '@spike/new-booking-model';
import Button from 'components/UI/V3/Button/Button';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import { RadioGroup, RadioOption } from 'components/UI/V3/Forms/RadioGroup/RadioGroup';
import styles from './RecurringEditScopeDialog.module.scss';

export type RecurringEditScopeNoun = 'booking' | 'appointment';

export interface RecurringEditScopeDialogProps {
    open: boolean;
    title: string;
    noun: RecurringEditScopeNoun;
    futureScopeDescription?: string;
    isSaving?: boolean;
    zIndex?: number;
    onCancel: () => void;
    onConfirm: (scope: ApplyScope) => void;
}

const DEFAULT_SCOPE: ApplyScope = 'this_only';

export const RecurringEditScopeDialog: FunctionComponent<RecurringEditScopeDialogProps> = ({
    open,
    title,
    noun,
    futureScopeDescription,
    isSaving = false,
    zIndex,
    onCancel,
    onConfirm
}) => {
    const [scope, setScope] = useState<ApplyScope>(DEFAULT_SCOPE);

    useEffect(() => {
        if (open) {
            setScope(DEFAULT_SCOPE);
        }
    }, [open]);

    if (!open) {
        return null;
    }

    const options: Array<RadioOption<ApplyScope>> = [
        { value: 'this_only', inlineLabel: `This ${noun} only` },
        { value: 'this_and_future', inlineLabel: `This and future ${noun}s`, description: futureScopeDescription }
    ];

    const handleScopeChange = (value: ApplyScope) => {
        if (!isSaving) {
            setScope(value);
        }
    };

    const handleCancel = () => {
        if (!isSaving) {
            onCancel();
        }
    };

    const handleConfirm = () => {
        if (!isSaving) {
            onConfirm(scope);
        }
    };

    return (
        <GenericModal
            zIndex={zIndex}
            className={styles.modal}
            onClose={handleCancel}
            footer={
                <div className={styles.footer}>
                    <Button
                        id="recurring_edit_scope_cancel_button"
                        size="large"
                        className={styles.button}
                        label="Cancel"
                        variant="black"
                        disabled={isSaving}
                        onClick={handleCancel}
                    />
                    <Button
                        id="recurring_edit_scope_confirm_button"
                        size="large"
                        className={styles.button}
                        label="Confirm"
                        variant="green"
                        isLoading={isSaving}
                        disabled={isSaving}
                        onClick={handleConfirm}
                    />
                </div>
            }
        >
            <div className={styles.content}>
                <span className={styles.title}>{title}</span>
                <RadioGroup<ApplyScope>
                    value={scope}
                    options={options}
                    className={styles.options}
                    onChange={handleScopeChange}
                />
            </div>
        </GenericModal>
    );
};

export default RecurringEditScopeDialog;
